import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { ISelectItem } from '@cakeiteasy/web-ui';
import { COUNTRY_CODE, IUserCompany, ngHelper } from '@cakeiteasy/web-utils';

import { CompanyApiService } from '../../../api-services/services/company-api.service';
import { LEGAL_COMPANY_TYPES } from '../../payment.constants';

const COMPANY_SEARCH_ERRORS = {
  ENK_NOT_ALLOWED: 'ENK_NOT_ALLOWED',
  NOT_FOUND: 'NOT_FOUND',
};

@Component({
  standalone: false,
  selector: 'company-searcher',
  templateUrl: './company-searcher.component.html',
  styleUrls: ['./company-searcher.component.scss'],
})
export class CompanySearcherComponent implements OnInit, OnChanges {
  @Input() countryCode?: string;
  @Input() showCancel = false;
  @Input() isForPayment = false;
  @Input() allowToChangeCountry = true;
  @Input() orgNumber: string = null;
  @Input() bakeryId: number = null;
  @Input() companyOrgNumberValidator: (orgNumber: string) => Promise<boolean> = null;
  @Input() allowEhk = false;
  @Input() selectedItem: any;
  @Input() showCompanySearcherLabel = true;
  @Input() allowToChangeCompany = true;
  @Input() compact = false;

  @Output() onCompanyFound = new EventEmitter<IUserCompany>();
  @Output() onCancel = new EventEmitter<void>();

  public COMPANY_SEARCH_ERRORS = COMPANY_SEARCH_ERRORS;

  public searchCompanyError = '';
  public allowToContinueWithOrgNumber = true;
  public foundCompany: IUserCompany = null;

  public pLoading: Promise<any>;
  public countryCodeItem: ISelectItem;
  public countryCodeItems: ISelectItem[] = [];
  public noResults: boolean;

  constructor(
    private _companyApi: CompanyApiService,
    private _translate: TranslateService,
  ) {
    this.countryCodeItems = [
      {
        label: _translate.instant('common.countries.norway'),
        value: 'NO',
        ref: 'NO',
        isDefaultValue: false,
      },
      {
        label: _translate.instant('common.countries.sweden'),
        value: 'SE',
        ref: 'SE',
        isDefaultValue: false,
      },
    ];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'countryCode', (code) => {
      this.countryCodeItem = this.countryCodeItems.find((country) => country.value === code);
    });
  }

  public ngOnInit(): void {
    if (this.countryCodeItem && this.countryCode) {
      this.countryCodeItem.value = this.countryCode;
    }
  }

  public searchCompanyByQuery(query: string): Observable<any> {
    return this._companyApi
      .searchCompanyByQuery(query, this.countryCodeItem.value)
      .pipe(map((response) => response.results))
      .pipe(catchError(() => of([])));
  }

  public async companySelected(selectedCompany: { id: string; name: string }) {
    const orgNumber = selectedCompany.id;
    let company = null;

    try {
      company = await (this.pLoading = this._searchCompanyByOrgNumber(selectedCompany));
    } catch (e) {
      company = {
        id: null,
        legalCompany: { orgNumber, name: selectedCompany.name },
      };
    }

    this.foundCompany = company as IUserCompany;

    if (!this.allowEhk && this.foundCompany.legalCompany.organizationForm === 'ENK') {
      this.foundCompany = null;
      return (this.searchCompanyError = COMPANY_SEARCH_ERRORS.ENK_NOT_ALLOWED);
    }

    if (!this.foundCompany.id && this.countryCodeItem.value === COUNTRY_CODE.NO) {
      return (this.searchCompanyError = COMPANY_SEARCH_ERRORS.NOT_FOUND);
    }

    this.searchCompanyError = '';

    return this.emitCompany();
  }

  public emitCompany() {
    const company = this.foundCompany;
    let companyPromise = Promise.resolve<IUserCompany>(company);

    if (!company.id) {
      const { orgNumber, name } = company.legalCompany;
      companyPromise = this._createCompany(orgNumber, name, this.countryCodeItem.value);
    }

    return (this.pLoading = companyPromise.then((foundCompany) => {
      this.onCompanyFound.emit(foundCompany);
    }));
  }

  private _searchCompanyByOrgNumber(companyInfo: { id: string; name: string }) {
    const { id: orgNumber, name: companyName } = companyInfo;
    const validation = this.companyOrgNumberValidator
      ? this.companyOrgNumberValidator(orgNumber)
      : Promise.resolve(true);

    return (this.pLoading = validation.then(async (canContinue) => {
      this.allowToContinueWithOrgNumber = canContinue;

      if (!canContinue) {
        return Promise.reject('Org number is forbidden');
      }

      return this._searchOrCreateCompany(orgNumber, companyName);
    }));
  }

  private _searchOrCreateCompany(orgNumber: string, companyName?: string): Promise<IUserCompany> {
    const type = this.bakeryId ? LEGAL_COMPANY_TYPES.BAKERY_CUSTOMER : LEGAL_COMPANY_TYPES.COMPANY;
    const params = {
      orgNumber,
      countryCode: this.countryCodeItem.value,
      companyType: type,
      bakeryId: this.bakeryId,
      companyName,
    };

    return firstValueFrom(this._companyApi.searchOrCreateCompany(params));
  }

  private _createCompany(orgNumber: string, name: string, country: string): Promise<IUserCompany> {
    const type = this.bakeryId ? LEGAL_COMPANY_TYPES.BAKERY_CUSTOMER : LEGAL_COMPANY_TYPES.COMPANY;
    const legalCompany = { orgNumber, country, type, name };

    return this.bakeryId
      ? firstValueFrom(this._companyApi.createBakeryCompany(this.bakeryId, legalCompany))
      : firstValueFrom(this._companyApi.createCompany(legalCompany));
  }
}
